import React from 'react';
import {
  classes,
  createId,
  HelperText,
  useSlug,
} from '@bloomberg/media-ui-core';
import type { StateChangeOptions } from 'downshift';
import Downshift from 'downshift';

import { DropdownOption } from '../DropdownOption';
import { DropdownOption as DropdownOptionType } from '../props';

import { SearchDropdownInnerProps, SearchDropdownProps } from './props';

import styles from './SearchDropdown.module.css';

function inputStringToRenderAfterSelection(item: DropdownOptionType | null) {
  return item?.displayValue || item?.value || '';
}

// Inner component
// We are forwarding the ref here so that Downshift
// can attach correctly
const Dropdown = React.forwardRef<HTMLDivElement, SearchDropdownInnerProps>(
  function Dropdown(props, ref) {
    const {
      options,
      noOptionsText = 'No items to display',
      renderProps,
      size,
      inputProps,
      labelProps,
      unstyled = false,
      id,
      children,
      dropdownVariant,
      showDropdownPlusBtn,
    } = props;

    const {
      getInputProps,
      getLabelProps,
      getRootProps,
      getMenuProps,
      getItemProps,
      highlightedIndex,
      isOpen,
    } = renderProps;

    const menuProps = {
      ...getMenuProps(),
      'aria-labelledby': `${id}-menu`,
    };

    const query = getInputProps<DropdownOptionType>().value || '';
    const shouldShowDropdown =
      isOpen && (options.length > 0 || (options.length === 0 && query));

    return (
      <>
        <div
          className={classes([styles['input-container']], { unstyled })}
          ref={ref}
        >
          {children({
            query,
            labelProps: {
              ...getLabelProps(),
              ...labelProps,
            },
            inputProps: {
              ...inputProps,
              autoComplete: 'off',
              'aria-labelledby': `${id}-label`,
              // manually ensure that downshift
              // methods are called
              onBlur: (ev: React.FocusEvent<HTMLInputElement>) => {
                ev.persist();

                // we need to manually call inputProps here
                // since we are overriding the onBlur
                if (inputProps?.onBlur) {
                  inputProps.onBlur(ev);
                }
              },
              onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
                const { onChange } = getInputProps();

                ev.persist();

                if (onChange) {
                  onChange(ev);
                }

                // handle possible inputProps event
                if (inputProps?.onChange) {
                  inputProps.onChange(ev);
                }
              },
              onFocus: (ev) => {
                if (inputProps?.onFocus) {
                  inputProps.onFocus(ev);
                }
              },
              onKeyDown: (ev: React.KeyboardEvent<HTMLInputElement>) => {
                const { onKeyDown } = getInputProps();

                if (onKeyDown) {
                  onKeyDown(ev);
                }

                if (inputProps?.onKeyDown) {
                  inputProps.onKeyDown(ev);
                }
              },
            },
            getRootProps: () => getRootProps({}, { suppressRefError: true }),
          })}
        </div>

        <ul
          className={classes(
            [
              styles.menu,
              {
                [styles['menu-open']]: shouldShowDropdown,
                // helper classes, not used for styling
                open: isOpen,
                closed: !shouldShowDropdown,
              },
            ],
            props
          )}
          {...menuProps}
        >
          {options.length === 0 && query && (
            <li
              className={classes(
                [
                  styles.item,
                  styles.empty,
                  { [styles.small]: size === 'extraSmall' || size === 'small' },
                ],
                { unstyled }
              )}
            >
              {noOptionsText}
            </li>
          )}
          {options.length > 0 &&
            options.map((item, index) => (
              <DropdownOption
                key={`${item.value}${index}`}
                isHighlighted={index === highlightedIndex}
                value={item.value}
                subText={item.subText}
                displayValue={item.displayValue}
                variant={dropdownVariant}
                {...getItemProps({ item, index })}
                url={item.url}
                showPlusIcon={showDropdownPlusBtn}
              />
            ))}
        </ul>
      </>
    );
  }
);

export const SearchDropdown = React.forwardRef<
  HTMLDivElement,
  SearchDropdownProps
>(function Component(props, ref) {
  const {
    disabled = false,
    name: userProvidedName,
    id: userProvidedId,
    inputProps,
    labelProps,
    noOptionsText,
    options = [],
    onChange: onChangeProp,
    onTextChange,
    placeholder = '',
    label = '',
    size = 'default',
    helperText,
    variant,
    inputValue,
    onClear,
    children,
    dropdownVariant,
    showDropdownPlusBtn,
  } = props;

  // determines whether or not to show results
  // this extends the base downshift behavior
  // to act more similarly to material-ui, where the drawer shows
  // all results after the focus is blurred.
  const stableId = React.useRef(createId());

  // create a unique id for inputs
  const id = userProvidedId || stableId.current;

  const name = userProvidedName || 'OutlinedDropdown';
  // stable name for data-component attribute
  const [slugName] = useSlug(name);

  // Watch for state changes on downshift
  // and close the menu on any blur events
  const handleStateChange = React.useCallback(
    (state: StateChangeOptions<DropdownOptionType>) => {
      // close the drawer on the following events
      if (
        state.type === Downshift.stateChangeTypes.clickItem ||
        state.type === Downshift.stateChangeTypes.mouseUp ||
        state.type === Downshift.stateChangeTypes.blurButton ||
        state.type === Downshift.stateChangeTypes.blurInput
      ) {
        return {
          ...state,
          isOpen: false,
        };
      }

      return state;
    },
    []
  );

  // main component return
  return (
    <div
      ref={ref}
      data-component={slugName}
      id={`select-box-${label}`}
      className={classes([styles.wrapper], props)}
    >
      <Downshift
        inputId={id}
        labelId={`${id}-label`}
        menuId={`${id}-menu`}
        onChange={onChangeProp}
        onInputValueChange={onTextChange}
        inputValue={inputValue}
        onStateChange={handleStateChange}
        itemToString={inputStringToRenderAfterSelection}
      >
        {(renderProps) => (
          <Dropdown
            size={variant === 'markets' ? 'extraSmall' : size}
            disabled={disabled}
            renderProps={renderProps}
            name={name}
            label={label}
            id={id}
            placeholder={placeholder}
            options={options}
            noOptionsText={noOptionsText}
            unstyled={props.unstyled}
            inputProps={inputProps}
            labelProps={labelProps}
            onChange={onChangeProp}
            onClear={onClear}
            // we are marking the input helpertext
            // as undefined so that it does NOT
            // render. We are instead
            // rendering it directly in the dropdown
            helperText={undefined}
            dropdownVariant={dropdownVariant}
            showDropdownPlusBtn={showDropdownPlusBtn}
            {...renderProps.getRootProps()}
          >
            {children}
          </Dropdown>
        )}
      </Downshift>
      {helperText && (
        <HelperText text={helperText} className={styles.helperText} />
      )}
    </div>
  );
});
